import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { GripVertical, Trash2, Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { Menu, MenuItem as MuiMenuItem } from '@mui/material';
import { Button } from '@/components/ui/button';
import { ItemErrorText } from './ItemErrorText';
import { RowInput } from './RowInput';
import type { LinkRow, LinkRowErrors } from './MenuBuilderCore';
import { LABEL_ADD_EXTERNAL_URL, LABEL_ADD_HEALTHERA_FEATURE, LABEL_ADD_PAGE } from './labels';
import { withScheme } from './menuUrl';

interface SortableGroupLinkProps {
  id: UniqueIdentifier;
  link: LinkRow;
  ro?: boolean;
  errors?: LinkRowErrors;
  onLabelChange: (v: string) => void;
  onUrlChange: (v: string) => void;
  onRemove: () => void;
  onAddExternal: () => void;
  onAddFeature: () => void;
  onAddPage: () => void;
}

export const SortableGroupLink = ({
  id,
  link,
  ro,
  errors,
  onLabelChange,
  onUrlChange,
  onRemove,
  onAddExternal,
  onAddFeature,
  onAddPage,
}: SortableGroupLinkProps): ReactElement => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: ro,
  });

  const editableFields = link.isReadOnly ? (
    <span className="vtwo:flex-1 vtwo:text-sm vtwo:text-gray-700 vtwo:truncate">{link.label}</span>
  ) : (
    <>
      <RowInput
        value={link.label}
        onChange={onLabelChange}
        placeholder="Title"
        disabled={ro}
        error={Boolean(errors?.label)}
      />
      <RowInput
        value={link.url}
        onChange={onUrlChange}
        complete={withScheme}
        placeholder="URL"
        disabled={ro}
        error={Boolean(errors?.url)}
      />
    </>
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
    >
      <div className="vtwo:flex vtwo:items-center vtwo:gap-2">
        <div
          {...attributes}
          {...listeners}
          className="vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
        >
          <GripVertical className="vtwo:h-4 vtwo:w-4 vtwo:text-gray-400" />
        </div>
        {editableFields}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="vtwo:h-7 vtwo:w-7 vtwo:shrink-0"
          onClick={onRemove}
          disabled={ro}
        >
          <Trash2 className="vtwo:h-3 vtwo:w-3 vtwo:text-gray-400" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="vtwo:h-7 vtwo:w-7 vtwo:shrink-0"
          onClick={(e) => setAnchor(e.currentTarget)}
          disabled={ro}
        >
          <Plus className="vtwo:h-3 vtwo:w-3" />
        </Button>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          <MuiMenuItem
            onClick={() => {
              onAddExternal();
              setAnchor(null);
            }}
          >
            {LABEL_ADD_EXTERNAL_URL}
          </MuiMenuItem>
          <MuiMenuItem
            onClick={() => {
              onAddFeature();
              setAnchor(null);
            }}
          >
            {LABEL_ADD_HEALTHERA_FEATURE}
          </MuiMenuItem>
          <MuiMenuItem
            onClick={() => {
              onAddPage();
              setAnchor(null);
            }}
          >
            {LABEL_ADD_PAGE}
          </MuiMenuItem>
        </Menu>
      </div>
      <ItemErrorText messages={[errors?.label, errors?.url]} />
    </div>
  );
};
